import React, { useCallback, useState } from 'react';
import { Button, Stack, Box, Typography, Alert } from '@mui/material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { withStyles } from '@mui/styles';
import { useDropzone } from 'react-dropzone';
import { COLORS } from '../../utils/Application_Constants';

const styles = () => ({
    uploadContainer: {
        backgroundColor: `${COLORS.TRUE_BLUE}0D`,
        border: `dashed 2px ${COLORS.TRUE_BLUE}40`,
        padding: '26px 33px',
        borderRadius: '8px',
        width: '100%',
        transition: 'background-color 0.2s ease-in-out',
    },
    uploadIcon: {
        width: '35px',
        height: '35px',
        borderRadius: '50%',
        backgroundColor: COLORS.LT_JOURNEY10,
        color: COLORS.LT_JOURNEY,
        display: 'flex',
    },
});

function UploadFile({
    classes,
    isViewOnly = false,
    isDisabled = false,
    maxSizeInMB = 10,
    fileTypes = {
        'image/jpeg': [],
        'image/png': [],
        'image/heic': [],
        'application/pdf': [],
    },
    fileTypesLabels = '.pdf, .heic, .jpeg, .png',
    onAcceptedFiles,
}) {
    const maxSize = maxSizeInMB * 1048576;
    const [errorMessages, setErrorMessages] = useState([]);
    const [dragging, setDragging] = useState(false);

    // custom file validation and error messages
    const customValidation = (file) => {
        const isValidSize = file.size <= maxSize;
        if (!isValidSize)
            return {
                code: 'invalid-file-size',
                message: `This file must be ${maxSizeInMB}MB or less`,
            };
        return null;
    };

    // TODO: WIRE THIS UP WHEN BACKEND IS READY (IF WE DO NEED THIS)
    const onError = useCallback((err) => console.log(err));

    const onDropRejected = useCallback((rejections) => {
        const newErrorMessages = [];
        rejections.forEach((rejection) => {
            rejection.errors.forEach((error) => {
                if (error.code === 'file-invalid-type') {
                    newErrorMessages.push(
                        `${
                            rejections.length > 1 ? `${rejection.file.name} - ` : ''
                        }The file must be in ${fileTypesLabels}`
                    );
                } else {
                    newErrorMessages.push(
                        `${rejections.length > 1 ? `${rejection.file.name} - ` : ''}${
                            error.message
                        }`
                    );
                }
            });
        });
        setErrorMessages(newErrorMessages);
    });

    const onDrop = useCallback((acceptedFiles) => {
        setErrorMessages([]);
        onAcceptedFiles(acceptedFiles);
    });

    const handleDragEnter = () => {
        setDragging(true);
    };

    const handleDragLeave = () => {
        setDragging(false);
    };

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        onError,
        onDropRejected,
        accept: fileTypes,
        disabled: isViewOnly || isDisabled,
        validator: customValidation,
        onDragEnter: handleDragEnter,
        onDragLeave: handleDragLeave,
    });

    return (
        <>
            <Button
                sx={{
                    '&.MuiButtonBase-root:hover': {
                        bgcolor: 'transparent',
                        color: 'inherit',
                    },
                    '&.MuiButtonBase-root': {
                        padding: '0',
                    },
                }}
                {...getRootProps({
                    className: `${(isViewOnly || isDisabled) && 'disabled'}`,
                })}
            >
                <Stack
                    className={classes.uploadContainer}
                    alignItems="center"
                    justifyContent="center"
                    gap="10px"
                    sx={{
                        backgroundColor: dragging ? `${COLORS.TRUE_BLUE}26 !important` : 'inherit',
                    }}
                >
                    <Box className={classes.uploadIcon} alignItems="center" justifyContent="center">
                        <FileUploadOutlinedIcon />
                    </Box>
                    <Stack direction="row" alignItems="center" justifyContent="center">
                        <Typography
                            fontSize="14px"
                            lineHeight="1.2"
                            sx={{
                                color: COLORS.LT_FOUNDATION,
                            }}
                        >
                            <u>Click to Upload</u>{' '}
                            <Box
                                component="span"
                                sx={{
                                    color: COLORS.LT_MIDNIGHT,
                                }}
                            >
                                or Drag and Drop
                            </Box>
                        </Typography>
                    </Stack>
                    <Typography
                        fontSize="12px"
                        lineHeight="1.2"
                        sx={{ color: COLORS.LT_MIDNIGHT75 }}
                    >
                        Max file size: {maxSizeInMB}MB | {fileTypesLabels}
                    </Typography>
                    <input
                        {...getInputProps()}
                        type="file"
                        disabled={isViewOnly || isDisabled}
                        aria-label="Click to Upload or Drag and Drop"
                    />
                </Stack>
            </Button>
            {errorMessages.map((error, index) => (
                <Alert
                    sx={{
                        backgroundColor: 'transparent',
                        padding: 0,
                        color: COLORS.LT_PEPPER150,
                        '& .MuiAlert-message, & .MuiAlert-icon': { padding: 0 },
                    }}
                    key={index} // eslint-disable-line react/no-array-index-key
                    severity="error"
                >
                    {error}
                </Alert>
            ))}
        </>
    );
}

export default withStyles(styles)(UploadFile);
